<template>
  <mc-dialog
    ref="confirmationDialog"
    class="confirmation-dialog"
    :submit-button-text="submitButtonText"
    :submit-button-class="submitButtonClass"
    :cancel-button-text="cancelButtonText"
    :cancel-button-class="cancelButtonClass"
    @submit="handleSubmitButtonClick"
    @cancel="handleCancelButtonClick"
  >
    <template #header>
      <section class="title-wrapper">
        <span :class="['confirmation-icon', titleIcon]"></span>
        <span class="title-dialog">
          <slot v-if="ifHasTitleSlot" name="title"></slot>
          <span v-else>{{ title }}</span>
        </span>
      </section>
    </template>

    <div v-if="message" class="information-message">
      {{ message }}
    </div>

  </mc-dialog>
</template>

<script lang="ts" setup>
import { computed, ref, toRefs, useSlots } from "vue";
import McDialog from "@/components/ui/McDialog/McDialog.vue";

interface IMcConfirmationDialogProps {
  title?: string;
  titleIcon?: string;
  message?: string;
  submitButtonText?: string;
  submitButtonClass?: string;
  cancelButtonText?: string;
  cancelButtonClass?: string;
}

const props = withDefaults(defineProps<IMcConfirmationDialogProps>(), {
  title: "Are you sure you wish to continue?",
  titleIcon: "icon-triangle-exclamation-warning-alert",
  message: "",
  submitButtonText: "Continue",
  submitButtonClass: "primary-warning-submit-button",
  cancelButtonText: "Cancel",
  cancelButtonClass: "secondary-cancel-button",
});

const {
  title,
  titleIcon,
  message,
  submitButtonText,
  submitButtonClass,
  cancelButtonText,
  cancelButtonClass,
} = toRefs(props);

const slots = useSlots() as ReturnType<typeof useSlots>;
const ifHasTitleSlot = computed(() => !!slots.title);

const confirmationDialog = ref();
const emit = defineEmits(['click:continue', 'click:cancel']);

const openDialog = async () => {
  confirmationDialog.value?.openDialog();
};

const closeDialog = async () => {
  confirmationDialog.value?.closeDialog();
};

const handleSubmitButtonClick = () => {
  closeDialog();
  emit('click:continue');
};

const handleCancelButtonClick = () => {
  closeDialog();
  emit('click:cancel');
};

defineExpose({
  openDialog,
  closeDialog
});
</script>

<style>
:root {
  --confirmation-dialog-title-wrapper-gap: 16px;
  --confirmation-dialog-title-icon-size: 32px;
  --confirmation-dialog-title-icon-color: var(--colour-icons-warning-subtle);
  --confirmation-dialog-message-font-size: var(--font-size-body-regular);
  --confirmation-dialog-message-font-style: normal;
  --confirmation-dialog-message-font-weight: var(--font-weight-body-regular);
  --confirmation-dialog-message-line-height: var(--line-height-m);
  --confirmation-dialog-message-margin: 16px 0 0 53px;
  --confirmation-dialog-actions-section-margin: 32px 0 0;
  --confirmation-dialog-cancel-button-width: 110px;
}
</style>

<style scoped lang="scss">
.confirmation-dialog {
  --dialog-actions-section-margin: var(--confirmation-dialog-actions-section-margin);

  .title-wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    gap: var(--confirmation-dialog-title-wrapper-gap);

    .confirmation-icon {
      color: var(--confirmation-dialog-title-icon-color);
      font-size: var(--confirmation-dialog-title-icon-size);
    }

    .title-dialog {
      max-width: calc(100% - 55px);
    }
  }

  .information-message {
    color: var(--colour-text-neutral-subtle);
    font-size: var(--confirmation-dialog-message-font-size);
    font-style: var(--confirmation-dialog-message-font-style);
    font-weight: var(--confirmation-dialog-message-font-weight);
    line-height: var(--confirmation-dialog-message-line-height);
    margin: var(--confirmation-dialog-message-margin);
  }

  :deep(.secondary-cancel-button) {
    --button-width: var(--confirmation-dialog-cancel-button-width);
  }
}
</style>